/**
 * The Today hero → Conversations handoff.
 *
 * In-memory rather than router state on purpose: attachments are base64 and
 * routinely multi-megabyte, and history state is serialized (and size-capped)
 * by the browser. A module-level slot survives SPA navigation — which is the
 * whole journey — and is intentionally lost on a hard refresh, exactly like
 * the old router-state read that cleared itself after one use.
 *
 * Write with `setHeroHandoff` immediately before navigating; the destination
 * calls `consumeHeroHandoff()` once, which empties the slot so a re-render or
 * a back/forward can never replay the send.
 */

import type { ConversationPosition } from '@/components/assistant/conversation-position'
import type { AssistantChatAttachment } from '@/lib/api/assistant.api'
import { captureRequestScope } from '@/lib/api/request-scope'
import { captureActivePageSnapshot } from '@/store/page-snapshot.store'

import type { PageSnapshot, SessionAnchor } from '@scrambl/shared'

export interface HeroHandoff {
  /** Explicitly open a blank chat when the popover has no persisted session. */
  newConversation?: boolean
  position?: ConversationPosition
  draft?: { text: string; attachments: AssistantChatAttachment[] }
  sessionId?: string
  prefill?: string
  anchor?: SessionAnchor
  pageSnapshot?: PageSnapshot
  ask?: string
  attachments?: AssistantChatAttachment[]
  openPage?: { path: string; title?: string }
}

let pending: HeroHandoff | null = null
let pendingOwner: AbortSignal | null = null

export function setHeroHandoff(h: HeroHandoff) {
  pending = Object.hasOwn(h, 'pageSnapshot')
    ? h
    : { ...h, pageSnapshot: captureActivePageSnapshot() }
  pendingOwner = captureRequestScope().signal
}

export function consumeHeroHandoff(): HeroHandoff | null {
  const h = pendingOwner?.aborted ? null : pending
  pending = null
  pendingOwner = null
  return h
}
